import { useState, useEffect, useCallback } from "react";
import { Button, Table } from "antd";
import { TicketPercent } from "lucide-react";
import { fetchDiscountCodes } from "services/discountCodes.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import CodeFormModal from "pages/DiscountCodes/_blocks/CodeFormModal";
import { getCodeListColumns } from "pages/DiscountCodes/commons/discountCodes.columns";
import {
  CODES_PAGE_SIZE,
  filterPortalCodes,
} from "pages/DiscountCodes/commons/discountCodes.helpers";
import { CODES_COPY } from "pages/DiscountCodes/commons/discountCodes.copy";

/** Codes that cover this event, plus "Create discount code" (R-7). */
function DiscountCodesTab({ eventId, communityId }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(
    async (page) => {
      setLoading(true);
      const { data: envelope, errors } = await fetchDiscountCodes({
        current_page: page,
        page_size: CODES_PAGE_SIZE,
        event_id: eventId,
      });
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        setCodes([]);
        return;
      }
      // Event codes only (D78 R11).
      setCodes(
        filterPortalCodes(Array.isArray(envelope?.data) ? envelope.data : [])
      );
      setPagination({
        current: page,
        total: envelope?.meta?.pagination?.total_count ?? 0,
      });
    },
    [eventId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const columns = getCodeListColumns({ showEvents: false, showActions: false });
  const createButton = (
    <Button type="primary" size="small" onClick={() => setModalOpen(true)}>
      {CODES_COPY.cta}
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-lg font-semibold">
          {CODES_COPY.eventTabHeading}
        </span>
        {createButton}
      </div>

      {!loading && codes.length === 0 ? (
        <div className="rounded-2xl border bg-card shadow-sm">
          <div className="p-12 text-center text-muted-foreground space-y-4">
            <TicketPercent className="h-12 w-12 mx-auto" />
            <p>{CODES_COPY.eventTabEmpty}</p>
            {createButton}
          </div>
        </div>
      ) : (
        <Table
          rowKey="code_id"
          columns={columns}
          dataSource={codes}
          loading={loading}
          size="small"
          scroll={{ x: 700 }}
          pagination={
            pagination.total > CODES_PAGE_SIZE
              ? {
                  current: pagination.current,
                  pageSize: CODES_PAGE_SIZE,
                  total: pagination.total,
                  showSizeChanger: false,
                  placement: "bottomRight",
                }
              : false
          }
          onChange={(pag) => load(pag.current)}
        />
      )}

      <CodeFormModal
        open={modalOpen}
        defaultEventId={eventId}
        defaultCommunityId={communityId ?? undefined}
        onClose={() => setModalOpen(false)}
        onSaved={() => load(1)}
      />
    </div>
  );
}

export default DiscountCodesTab;
